import { ArrowPathIcon, PencilIcon, CheckCircleIcon, XCircleIcon } from "../../../automationStudioIcons";
import ButtonComponent from "../../../../customButton";
import { useState } from "react";
import StepResultsTab from "./tabs/StepResultsTab";
import ScreenshotsTab from "./tabs/ScreenshotsTab";
import TimelineTab from "./tabs/TimelineTab";
import styles from "../../../../../styles/automationStudio/automationStudio.module.scss";
import { LABELS } from "../../../content";
import { formatDuration } from '../../../../AutomationStudio/utils';
import { computeExecutionSummaryStats } from '../../../../AutomationStudio/executionSummaryStats';
import NativeTabBar from '../../shared/NativeTabBar';

/**
 * CompletedExecutionView - Results display after execution completes
 * Matches legacy Automation Studio layout.
 */
export default function CompletedExecutionView({
    executionResults = null,
    overallStatus = null,
    instructions = [],
    replayProgressMap = {},
    replayExecutionResults = null,
    flatStepsForReplay = [],
    executionLogs = [],
    screenshots = [],
    executionFlow = [],
    testCompleted = false,
    automationError = null,
    onStepClick = null,
    onRetry = null,
    onEditInstructions = null,
    isReplayFlow = false,
    hasExecutedInSession = false,
    isRunning = false,
    isReplayExecuting = false,
    isGenerating = false,
}) {
    const [activeTab, setActiveTab] = useState(0);

    const {
        totalSteps,
        successSteps,
        failedSteps,
        skippedSteps,
        attemptedSteps,
        totalDuration,
        hasFailures,
        allSuccess,
        hasReplayProgress,
    } = computeExecutionSummaryStats({
        instructions,
        replayProgressMap,
        replayExecutionResults,
        executionResults,
        overallStatus,
        automationError,
    });

    const hasAutomationError = !!automationError;

    const hasAnyResults = testCompleted
        || totalSteps > 0
        || hasReplayProgress
        || hasAutomationError
        || (replayExecutionResults != null);

    const tabItems = [
        { value: 0, label: LABELS.TAB_STEP_RESULTS },
    ];

    if (!hasAnyResults) {
        return (
            <div className={styles.completedExecutionContainer}>
                <div className={styles.completedExecutionHeader}>
                    <div className={styles.resultsStatusSection}>
                        <div className={styles.resultsTextContainer}>
                            <span className={styles.resultsTitle} style={{ fontSize: '13px' }}>
                                {LABELS.EXECUTION_STOPPED_EARLY}
                            </span>
                            <span className={`${styles.resultsSubtitle} ${styles.asTypoCaption}`}>
                                No steps were generated. Edit your instructions and try again.
                            </span>
                        </div>
                    </div>
                    <div className={styles.resultsActions}>
                        {onEditInstructions && (
                            <ButtonComponent
                                ctaText={LABELS.EDIT_INSTRUCTIONS}
                                variant="secondary"
                                size="small"
                                className="slate900 bg-slate50"
                                startIcon={<PencilIcon className="icon slate900" />}
                                onClick={onEditInstructions}
                            />
                        )}
                        {/* {onRetry && (
                            <ButtonComponent
                                ctaText={LABELS.RUN_AGAIN}
                                variant="primary"
                                size="small"
                                className="white bg-primary"
                                startIcon={<ArrowPathIcon className="icon white" />}
                                onClick={onRetry}
                            />
                        )} */}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.completedExecutionContainer}>
            <div className={styles.completedExecutionHeader}>
                <div className={styles.resultsStatusSection}>
                    {allSuccess ? (
                        <>
                            <CheckCircleIcon className={styles.statusIconSuccess} />
                            <div className={styles.resultsTextContainer}>
                                <span className={styles.resultsTitle} style={{ fontSize: '13px' }}>
                                    {LABELS.EXECUTION_SUCCESS}
                                </span>
                                <span className={`${styles.resultsSubtitle} ${styles.asTypoCaption}`}>
                                    All {attemptedSteps} attempted steps executed in {formatDuration(totalDuration)}
                                    {skippedSteps > 0 ? ` · ${skippedSteps} skipped` : ''}
                                </span>
                            </div>
                        </>
                    ) : hasFailures ? (
                        <>
                            <XCircleIcon className={styles.statusIconFailed} />
                            <div className={styles.resultsTextContainer}>
                                <span className={styles.resultsTitle} style={{ fontSize: '13px', color: "#DC2626" }}>
                                    {LABELS.EXECUTION_FAILED}
                                </span>
                                <span className={`${styles.resultsSubtitle} ${styles.asTypoCaption}`}>
                                    {failedSteps} of {attemptedSteps} attempted steps failed
                                    {skippedSteps > 0 ? ` · ${skippedSteps} skipped` : ''}
                                </span>
                            </div>
                        </>
                    ) : (
                        <>
                            <CheckCircleIcon className={styles.statusIconPartial} />
                            <div className={styles.resultsTextContainer}>
                                <span className={styles.resultsTitle} style={{ fontSize: '13px' }}>
                                    {LABELS.EXECUTION_PARTIAL}
                                </span>
                                <span className={`${styles.resultsSubtitle} ${styles.asTypoCaption}`}>
                                    {successSteps} of {attemptedSteps} attempted steps succeeded
                                    {skippedSteps > 0 ? ` · ${skippedSteps} skipped` : ''}
                                </span>
                            </div>
                        </>
                    )}
                </div>

                <div className={styles.resultsActions}>
                    {onEditInstructions && (
                        <ButtonComponent
                            ctaText={LABELS.EDIT_INSTRUCTIONS}
                            variant="secondary"
                            size="small"
                            className="slate900 bg-slate50"
                            startIcon={<PencilIcon className="icon slate900" />}
                            onClick={onEditInstructions}
                        />
                    )}
                    {/* {onRetry && (
                        <ButtonComponent
                            ctaText={LABELS.RUN_AGAIN}
                            variant="primary"
                            size="small"
                            className="white bg-primary"
                            startIcon={<ArrowPathIcon className="icon white" />}
                            onClick={onRetry}
                        />
                    )} */}
                </div>
            </div>

            <div className={styles.resultsTabBar}>
                <div className={styles.tabBar}>
                    <NativeTabBar value={activeTab} onChange={setActiveTab} items={tabItems} />
                </div>
            </div>

            <div className={styles.resultsTabContent}>
                {activeTab === 0 && (
                    <StepResultsTab
                        instructions={instructions}
                        replayProgressMap={replayProgressMap}
                        replayExecutionResults={replayExecutionResults}
                        flatStepsForReplay={flatStepsForReplay}
                        onStepClick={onStepClick}
                        isReplayFlow={isReplayFlow}
                        hasExecutedInSession={hasExecutedInSession}
                        isRunning={isRunning}
                        isReplayExecuting={isReplayExecuting}
                        isGenerating={isGenerating}
                    />
                )}
            </div>
        </div>
    );
}
